<template>
  <div>
    <div class="instructions-title">
      Create New Polygon
      <div class="pull-right">
        <v-tooltip right>
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <v-icon small>fas fa-info-circle</v-icon>
            </span>
          </template>
          Map iconography will hide while you create your custom polygon: it
          will reappear when you apply your new polygon.
        </v-tooltip>
      </div>
    </div>
    <div v-if="!totalArea" class="instructions">
      Click on the map to begin creating your custom location. Add as many
      vertices as you like then click on the first or final vertex to finish the
      location.
    </div>
    <div class="instructions" v-if="totalArea">
      Your location's area is {{ totalArea.toLocaleString() }}
      <span v-if="distUnits === 'km'">km<sup>2</sup></span>
      <span v-if="distUnits === 'mi'">mi<sup>2</sup></span>
    </div>
    <div class="pull-right mt-2">
      <v-btn v-if="showEditButton" color="primary" small @click="editPolygon"
        >EDIT</v-btn
      >
      <v-btn
        :disabled="!valid"
        v-if="showApplyButton"
        class="mr-2"
        color="primary"
        @click="applyPolygon"
        small
        >APPLY</v-btn
      >
      <v-btn
        v-if="showClearButton"
        small
        color="primary"
        outlined
        @click="clearPolygon"
        >CLEAR</v-btn
      >
    </div>
  </div>
</template>

<script>
import { MI_TO_KM_CONVERSION_FACTOR } from "@/common/constants/maps";
import { mapGetters } from "vuex";
import {
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_USER,
} from "@/store";
import { addDrawControls, removeDrawControls } from "@/common/maps/maps";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { getFeatureArea, roundGeojsonDecimals } from "@/common/maps/utilities";
import { SET_DRAW_MODE } from "@/store/mutations.type";
import {
  getSearchPolygonFromGeojson,
  getGeojsonFromSearchPolygon,
} from "@/services/search/wod.service";

const DRAW_MODE = "draw_polygon";
const SIMPLE_SELECT_MODE = "simple_select";
const STATIC_MODE = "static";
const DIRECT_SELECT_MODE = "direct_select";
const EDIT_MODES = [DRAW_MODE, SIMPLE_SELECT_MODE, DIRECT_SELECT_MODE];

const HIJACKER_NAME = "search-polygon";

const EMITTED_ACTIONS = {
  APPLY: "apply",
  EDITING: "editing",
  CLEAR: "clear",
};

export default {
  name: "SearchPolygonEditor",
  components: {},
  props: {
    currenttab: {
      type: String,
    },
    polygon: {
      type: String,
      required: false,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    mapped: false,
    applied: false,
    valid: false,
    mode: "editing",
    localPolygon: null,
    verticesPresent: false,

    currentDrawMode: null,
    mapClickListenerCallback: null,
  }),
  async mounted() {
    if (this.polygon) {
      this.localPolygon = this.polygon;
    }
    this.setUpDrawTools();
  },
  beforeDestroy() {
    this.tearDown();
  },
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    showEditButton() {
      if (EDIT_MODES.includes(this.currentDrawMode)) {
        return false;
      }
      return true;
    },
    showApplyButton() {
      return this.valid && this.currentDrawMode !== STATIC_MODE;
    },
    showClearButton() {
      return this.verticesPresent;
    },
    distUnits() {
      return this.preferences?.poisDistUnits === "miles" ? "mi" : "km";
    },
    totalArea() {
      let area = false;
      if (this.localPolygon?.geometry) {
        area = getFeatureArea(this.localPolygon);
        if (this.distUnits === "mi") {
          area = area * MI_TO_KM_CONVERSION_FACTOR;
        }
        area = Math.round(area / 1000000);
      }

      return area;
    },
  },
  methods: {
    setUpDrawTools() {
      //Skip if draw controls are already set up
      if (this.map._searchDrawControls) {
        this.drawControls = this.map._searchDrawControls;
        return;
      }

      this.drawControls = addDrawControls(this.map, {
        displayControlsDefault: false,
        defaultMode: this.polygon ? STATIC_MODE : DRAW_MODE,
        controls: {
          polygon: false,
          trash: false,
        },
        createEvent: this.mapCreateEvent,
        deleteEvent: this.mapDeleteEvent,
        updateEvent: this.mapUpdateEvent,
        actionableEvent: this.mapActionableEvent,
        modeChangeEvent: this.modeChangeEvent,
        selectionChangeEvent: this.selectionChangeEvent,
      });

      this.currentDrawMode = this.drawControls.getMode();
      console.log(
        "SearchPolygonEditor setUpDrawTools setting currentDrawMode",
        this.currentDrawMode
      );

      this.mapClickListenerCallback = (e) => {
        this.updateState();
      };
      this.map.on("click", this.mapClickListenerCallback);

      if (!this.polygon) {
        this.editPolygon();
      }
    },
    mapCreateEvent() {
      this.updateState();
    },
    mapDeleteEvent() {
      this.updateState();
    },
    mapUpdateEvent() {
      this.updateState();
    },
    mapActionableEvent() {
      this.updateState();
    },
    modeChangeEvent(a, b, c) {
      if (
        a.mode === SIMPLE_SELECT_MODE &&
        !this?.drawControls?.getAll()?.features?.[0]
      ) {
        this.changeDrawMode(DRAW_MODE);
      } else {
        this.updateState();
      }
    },
    selectionChangeEvent(a) {
      //We don't want the user to unselect the polygon. So if they do we reselect >:)
      if (!a?.features?.[0]) {
        this.editPolygon();
      }
      this.updateState();
    },
    editPolygon() {
      this.$emit(EMITTED_ACTIONS.EDITING, true);
      this.$store.commit(MODULE_PREFIX_MAP + SET_DRAW_MODE, true);
      if (!this.drawControls) {
        this.setUpDrawTools();
      }
      let existingPolygon = this.drawControls.getAll()?.features?.[0];
      let modeToUse = DRAW_MODE;
      let options = {};
      if (
        existingPolygon?.id &&
        existingPolygon?.geometry?.coordinates?.[0]?.[0] !== null
      ) {
        options.featureIds = [existingPolygon.id];
        modeToUse = SIMPLE_SELECT_MODE;
      } else if (this.polygon) {
        let polygonToDraw = getGeojsonFromSearchPolygon(this.polygon);
        polygonToDraw.id = "custom_polygon";
        this.drawControls.add(polygonToDraw);
        options.featureIds = [polygonToDraw.id];
        modeToUse = SIMPLE_SELECT_MODE;
      }
      this.changeDrawMode(modeToUse, options);
    },
    applyPolygon() {
      let polygonToSave = this.drawControls.getAll()?.features?.[0];
      this.changeDrawMode(STATIC_MODE);
      if (polygonToSave) {
        this.localPolygon = polygonToSave;
      }
      this.$emit(
        EMITTED_ACTIONS.APPLY,

        getSearchPolygonFromGeojson(
          roundGeojsonDecimals(JSON.parse(JSON.stringify(this.localPolygon)), 4)
        )
      );
      this.drawControls.deleteAll();

      this.$emit(EMITTED_ACTIONS.EDITING, false);
      this.$store.commit(MODULE_PREFIX_MAP + SET_DRAW_MODE, false);
    },
    clearPolygon() {
      this.drawControls.deleteAll();
      this.changeDrawMode(DRAW_MODE);
      this.localPolygon = null;
      this.updateState();
      if (this.polygon) {
        this.$emit(EMITTED_ACTIONS.APPLY, null);
      }
    },
    updateState() {
      setTimeout(() => {
        if (this.drawControls) {
          this.currentDrawMode = this.drawControls.getMode();
          this.$emit(
            EMITTED_ACTIONS.EDITING,
            this.currentDrawMode !== STATIC_MODE
          );
          let polygonCoordinates = this.drawControls.getAll()?.features?.[0]
            ?.geometry?.coordinates?.[0];
          this.verticesPresent =
            polygonCoordinates &&
            polygonCoordinates.length > 0 &&
            polygonCoordinates[0] !== null;
          this.valid =
            this.verticesPresent && this.currentDrawMode !== DRAW_MODE;
        }
      }, 0);
    },
    updateCurrentDrawMode() {
      setTimeout(() => {
        this.currentDrawMode = this.drawControls.getMode();
      }, 0);
    },
    changeDrawMode(modeToUse, options = {}) {
      setTimeout(() => {
        if (this.drawControls) {
          this.drawControls.changeMode(modeToUse, options);
          this.updateState();
        }
      }, 0);
    },
    tearDown() {
      this?.drawControls?.deleteAll();
      removeDrawControls(this.map, this.drawControls);
      this.drawControls = null;
      removeEventListener("click", this.mapClickListenerCallback);
      this.$store.commit(MODULE_PREFIX_MAP + SET_DRAW_MODE, false);
      this.$emit(EMITTED_ACTIONS.EDITING, false);
      this.localPolygon = null;
      this.valid = false;
      this.verticesPresent = false;
    },
  },
  watch: {
    isOpen: {
      handler() {
        if (!this.isOpen && this.drawControls) {
          this.tearDown();
        } else if (!this.localPolygon && !this.polygon) {
          this.setUpDrawTools();
        }
      },
    },
    polygon: {
      handler() {
        this.tearDown();
        this.setUpDrawTools();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.instructions-title {
  font-size: small;
}
.instructions {
  font-size: x-small;
}
</style>
